import { useState } from "react";
import { Link } from "react-router-dom";
import { DataTable, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { approveApprovalRequest, listApprovalRequests, rejectApprovalRequest } from "../../api/approvalsApi";
import type { ApprovalRequestOut } from "../../api/types";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";

const STATUS_BADGE_CLASS: Record<string, string> = {
  PENDING: "badge--due-soon",
  APPROVED: "badge--current",
  REJECTED: "badge--overdue",
};

/**
 * Approval Requests review queue — `GET /approval-requests`,
 * `POST /approval-requests/{id}/{approve,reject}` (§11.2). **Highest-priority surface in
 * this pass**: since the approval-workflow backend can now silently create a Purchase
 * Order in `PENDING_APPROVAL` status (see `PurchaseOrdersPage`'s status badge / the "review"
 * link there), this queue is the only place that status is actually actionable.
 *
 * Gated by `require_permission("purchase_order.approve")`, NOT a hardcoded role list — the
 * platform-default seed grants this to OWNER only, but a tenant can grant it to another
 * role via a per-user permission override (`UserPermissionsPage`). Since there is no
 * "my effective permissions" self-check endpoint, this route is intentionally reachable by
 * every app-access role (see `roles/permissions.ts`) and this page itself handles a 403
 * gracefully instead of hiding behind a role guard that could hide it from a
 * legitimately-granted non-OWNER user.
 */
export function ApprovalRequestsPage() {
  const { ensureFreshToken } = useAuth();
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const requestsState = useAsyncData(
    () => ensureFreshToken().then((token) => listApprovalRequests(token, { status: statusFilter || undefined })),
    [statusFilter],
  );
  const [decidingId, setDecidingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  async function decide(request: ApprovalRequestOut, action: "approve" | "reject") {
    setActionError(null);
    setDecidingId(request.id);
    try {
      const token = await ensureFreshToken();
      if (action === "approve") await approveApprovalRequest(token, request.id);
      else await rejectApprovalRequest(token, request.id);
      requestsState.reload();
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) {
        setForbidden(true);
      } else {
        setActionError(error instanceof ApiError ? error.message : "Could not record decision.");
      }
    } finally {
      setDecidingId(null);
    }
  }

  const columns: DataTableColumn<ApprovalRequestOut, keyof ApprovalRequestOut>[] = [
    { key: "entity_type", header: "Entity", render: (r) => r.entity_type },
    {
      key: "entity_id",
      header: "Record",
      render: (r) =>
        r.entity_type === "PURCHASE_ORDER" ? (
          <Link to={`/purchase-orders/${r.entity_id}`}>{r.entity_id.slice(0, 8)}</Link>
        ) : (
          <span className="muted">{r.entity_id.slice(0, 8)}</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <span className={`badge ${STATUS_BADGE_CLASS[r.status] ?? "badge--current"}`}>{r.status}</span>,
    },
    { key: "created_at", header: "Requested", render: (r) => new Date(r.created_at).toLocaleString() },
    { key: "decided_at", header: "Decided", render: (r) => (r.decided_at ? new Date(r.decided_at).toLocaleString() : "—") },
    {
      key: "id",
      header: "",
      render: (r) =>
        r.status === "PENDING" ? (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="sl-button sl-button--primary sl-button--md"
              disabled={decidingId === r.id}
              onClick={() => void decide(r, "approve")}
            >
              Approve
            </button>
            <button
              type="button"
              className="sl-button sl-button--danger sl-button--md"
              disabled={decidingId === r.id}
              onClick={() => void decide(r, "reject")}
            >
              Reject
            </button>
          </div>
        ) : (
          <span className="muted">Decided</span>
        ),
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Approval Requests</h1>
          <p className="page__subtitle">
            A Purchase Order over an approval-rule threshold is created in <code>PENDING_APPROVAL</code> status and
            paired with a request here — nothing else in this app can move it forward except approving or
            rejecting it right here.
          </p>
        </div>
        <Link to="/approvals/rules" className="sl-button sl-button--secondary sl-button--md">
          Configure thresholds
        </Link>
      </div>

      {forbidden ? (
        <StatusBanner kind="info">
          Your role doesn't currently have the <code>purchase_order.approve</code> permission (platform default:
          OWNER only). An OWNER can grant it to you individually from Staff → Permissions.
        </StatusBanner>
      ) : null}
      {requestsState.error ? <StatusBanner kind="error">{requestsState.error}</StatusBanner> : null}
      {actionError ? <StatusBanner kind="error">{actionError}</StatusBanner> : null}

      <div className="filter-bar">
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="status-filter">
            Status
          </label>
          <select id="status-filter" className="sl-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="">All</option>
          </select>
        </div>
      </div>

      <div className="panel">
        {requestsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={requestsState.data ?? []}
            getRowId={(row) => row.id}
            emptyMessage="No approval requests match this filter."
            caption="Approval Requests"
          />
        )}
      </div>
    </div>
  );
}
